<app-collapsible-section
  title="UTM Tagging"
  [headerBadge]="platformRows.length > 0 ? urlCountLabel : ''"
  [isExpanded]="isExpanded"
  [isCompleted]="isCompleted"
  [showDoneButton]="false"
  (toggle)="onUtmTaggingToggle($event)"
  (done)="onUtmTaggingDone()"
>
  <ng-template #sectionContent>
    <div class="utm-tagging-content">
      <div class="utm-tagging-intro">
        <h4 class="utm-tagging-subtitle">URL &amp; UTM configuration</h4>
        <p class="utm-tagging-description">
          Each platform gets its own campaign URL. UTM parameters are editable per row — updates
          save automatically and propagate to the live campaign.
        </p>
      </div>

      <div *ngIf="platformRows.length === 0" class="utm-empty-state">
        <p>Select at least one platform to generate campaign URLs.</p>
      </div>

      <div *ngIf="platformRows.length > 0" class="utm-platform-list">
        <div
          *ngFor="let row of platformRows; trackBy: trackByPlatformId"
          class="utm-platform-row"
          [class.expanded]="row.isExpanded"
        >
          <div class="utm-platform-row-header">
            <div class="utm-platform-row-main">
              <div class="utm-platform-icon" [ngSwitch]="row.iconType">
                <img *ngSwitchCase="'google'" src="/assets/images/googleads.svg" [alt]="row.name" />
                <img
                  *ngSwitchCase="'facebook'"
                  src="/assets/images/facebook.svg"
                  [alt]="row.name"
                />
                <svg
                  *ngSwitchCase="'email'"
                  width="26"
                  height="20"
                  viewBox="0 0 26 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M1.75 3.75C1.75 3.08696 2.01339 2.45107 2.48223 1.98223C2.95107 1.51339 3.58696 1.25 4.25 1.25H21.75C22.413 1.25 23.0489 1.51339 23.5178 1.98223C23.9866 2.45107 24.25 3.08696 24.25 3.75V16.25C24.25 16.913 23.9866 17.5489 23.5178 18.0178C23.0489 18.4866 22.413 18.75 21.75 18.75H4.25C3.58696 18.75 2.95107 18.4866 2.48223 18.0178C2.01339 17.5489 1.75 16.913 1.75 16.25V3.75Z"
                    stroke="#101223"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M1.75 3.75L13 11.25L24.25 3.75"
                    stroke="#101223"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  *ngSwitchCase="'sms'"
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M13.75 3.75H26.25V13.75H22.5L17.5 16.25V13.75H13.75V3.75Z"
                    stroke="#101223"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M18.75 20V25C18.75 25.3315 18.6183 25.6495 18.3839 25.8839C18.1495 26.1183 17.8315 26.25 17.5 26.25H7.5C7.16848 26.25 6.85054 26.1183 6.61612 25.8839C6.3817 25.6495 6.25 25.3315 6.25 25V7.5C6.25 7.16848 6.3817 6.85054 6.61612 6.61612C6.85054 6.3817 7.16848 6.25 7.5 6.25H10M12.5 22.5V22.5125"
                    stroke="#101223"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  *ngSwitchCase="'mailer'"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 21V14.5C10 13.5717 9.63125 12.6815 8.97487 12.0251C8.3185 11.3687 7.42826 11 6.5 11M6.5 11C5.57174 11 4.6815 11.3687 4.02513 12.0251C3.36875 12.6815 3 13.5717 3 14.5V21H21V15C21 13.9391 20.5786 12.9217 19.8284 12.1716C19.0783 11.4214 18.0609 11 17 11H6.5ZM12 11V3H16L18 5L16 7H12M6 15H7"
                    stroke="#354052"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <span class="utm-platform-name">{{ row.name }}</span>
              <ng-container *ngIf="landingPageReady; else trackingLinkPending">
                <span class="utm-platform-url" [title]="row.url">{{ truncateUrl(row.url) }}</span>

                <button
                  type="button"
                  class="utm-copy-btn"
                  aria-label="Copy URL"
                  (click)="copyUrl(row.url, $event)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M5.33333 5.33333H3.33333C2.97971 5.33333 2.64057 5.47381 2.39052 5.72386C2.14048 5.9739 2 6.31304 2 6.66667V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H9.33333C9.68696 14 10.0261 13.8595 10.2761 13.6095C10.5262 13.3594 10.6667 13.0203 10.6667 12.6667V10.6667M6.66667 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V9.33333C14 9.68696 13.8595 10.0261 13.6095 10.2761C13.3594 10.5262 13.0203 10.6667 12.6667 10.6667H6.66667C6.31304 10.6667 5.9739 10.5262 5.72386 10.2761C5.47381 10.0261 5.33333 9.68696 5.33333 9.33333V3.33333C5.33333 2.97971 5.47381 2.64057 5.72386 2.39052C5.9739 2.14048 6.31304 2 6.66667 2Z"
                      stroke="#15172E"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span class="utm-copy-btn-label">Copy</span>
                </button>
              </ng-container>
              <ng-template #trackingLinkPending>
                <span class="utm-platform-url-pending">
                  Your tracking link will appear here once the landing page is created.
                </span>
              </ng-template>

              <span class="utm-ready-badge" *ngIf="row.isReady">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M11.6667 3.5L5.25 9.91667L2.33333 7"
                    stroke="#079271"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Ready
              </span>
            </div>

            <button
              type="button"
              class="utm-row-chevron"
              [class.expanded]="row.isExpanded"
              [attr.aria-expanded]="row.isExpanded"
              [attr.aria-label]="row.isExpanded ? 'Collapse UTM details' : 'Expand UTM details'"
              (click)="toggleRowExpand(row, $event)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="#717683"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div class="utm-platform-row-details" *ngIf="row.isExpanded">
            <div class="utm-details-grid">
              <div class="utm-details-form">
                <div class="utm-field">
                  <label class="utm-field-label" [for]="'utm_source_' + row.platformId">
                    utm_source <span class="required">*</span>
                  </label>
                  <input
                    class="utm-field-input"
                    [class.error]="row.validationErrors.utm_source"
                    type="text"
                    [id]="'utm_source_' + row.platformId"
                    [(ngModel)]="row.params.utm_source"
                    (ngModelChange)="onParamChange(row)"
                  />
                  <div class="error-message" *ngIf="row.validationErrors.utm_source">
                    {{ row.validationErrors.utm_source }}
                  </div>
                </div>
                <div class="utm-field">
                  <label class="utm-field-label" [for]="'utm_medium_' + row.platformId">
                    utm_medium <span class="required">*</span>
                  </label>
                  <input
                    class="utm-field-input"
                    [class.error]="row.validationErrors.utm_medium"
                    type="text"
                    [id]="'utm_medium_' + row.platformId"
                    [(ngModel)]="row.params.utm_medium"
                    (ngModelChange)="onParamChange(row)"
                  />
                  <div class="error-message" *ngIf="row.validationErrors.utm_medium">
                    {{ row.validationErrors.utm_medium }}
                  </div>
                </div>
                <div class="utm-field">
                  <label class="utm-field-label" [for]="'utm_campaign_' + row.platformId">
                    utm_campaign <span class="required">*</span>
                  </label>
                  <input
                    class="utm-field-input"
                    [class.error]="row.validationErrors.utm_campaign"
                    type="text"
                    [id]="'utm_campaign_' + row.platformId"
                    [(ngModel)]="row.params.utm_campaign"
                    (ngModelChange)="onParamChange(row)"
                  />
                  <div class="error-message" *ngIf="row.validationErrors.utm_campaign">
                    {{ row.validationErrors.utm_campaign }}
                  </div>
                </div>

                <div class="utm-optional-fields">
                  <div class="utm-field">
                    <label class="utm-field-label" [for]="'utm_content_' + row.platformId">
                      utm_content (optional)
                    </label>
                    <input
                      class="utm-field-input"
                      type="text"
                      [id]="'utm_content_' + row.platformId"
                      [(ngModel)]="row.params.utm_content"
                      (ngModelChange)="onParamChange(row)"
                    />
                  </div>
                  <div class="utm-field utm-field-last">
                    <label class="utm-field-label" [for]="'utm_term_' + row.platformId">
                      utm_term (optional)
                    </label>
                    <input
                      class="utm-field-input"
                      type="text"
                      [id]="'utm_term_' + row.platformId"
                      [(ngModel)]="row.params.utm_term"
                      (ngModelChange)="onParamChange(row)"
                    />
                  </div>
                </div>
              </div>

              <div class="utm-live-preview">
                <div class="utm-live-preview-header">
                  <div>
                    <h5 class="utm-live-preview-title">Live preview</h5>
                    <p class="utm-live-preview-hint">Update as you type</p>
                  </div>
                  <span class="utm-preview-ready-badge" *ngIf="row.isReady">Ready to use</span>
                </div>

                <div class="utm-preview-url-box" *ngIf="landingPageReady; else previewUrlPending">
                  <span class="url-base">{{ getPreviewBaseUrl(row) }}</span>
                  <ng-container *ngFor="let key of utmParamKeys; let last = last">
                    <span class="url-key">{{ key }}</span
                    ><span class="url-eq">=</span
                    ><span class="url-value">{{ getPreviewParamValue(row, key) }}</span
                    ><span *ngIf="!last" class="url-amp">&amp;</span>
                  </ng-container>
                </div>
                <ng-template #previewUrlPending>
                  <p class="utm-preview-url-pending">
                    Your tracking link will appear here once the landing page is created.
                  </p>
                </ng-template>

                <button
                  *ngIf="landingPageReady"
                  type="button"
                  class="utm-copy-link-btn"
                  (click)="copyFullUrl(row, $event)"
                >
                  Copy Link
                </button>

                <div class="utm-preview-footer">
                  <span class="utm-char-count" *ngIf="landingPageReady"
                    >{{ getUrlCharacterCount(row) }} characters</span
                  >
                  <button type="button" class="utm-reset-btn" (click)="resetToDefault(row, $event)">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.33333 7C2.33333 4.42267 4.42267 2.33333 7 2.33333C8.41421 2.33333 9.68571 2.905 10.5858 3.805M11.6667 7C11.6667 9.57733 9.57733 11.6667 7 11.6667C5.58579 11.6667 4.31429 11.095 3.41421 10.195M2.33333 2.33333V5.66667H5.66667M8.33333 11.6667V8.33333H11.6667"
                        stroke="#1B5BFF"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Reset to default
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-template>
</app-collapsible-section>
